import { Suspense, lazy, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ReauthModal } from '@/components/auth/ReauthModal';
import { RealtimeConnection } from '@/components/common/RealtimeConnection';
import { SignupGate } from '@/components/auth/SignupGate';
import { useWebVitals } from '@/hooks/useWebVitals';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useSessionExpiration } from '@/hooks/useSessionExpiration';
import { useAuth } from '@/hooks/useAuth';
import { useAttributionReport } from '@/hooks/useAttributionReport';
import { useCrossTabSession } from '@/hooks/useCrossTabSession';
import { migrateNotifyPreference } from '@/lib/migrateNotifyPreference';
import { toast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { isOrgsEnabled } from '@/features/orgs/flag';
import { WorkspaceScope } from '@/features/orgs/components/WorkspaceScope';

// Lazy load all pages for code splitting
const Home = lazy(() => import('@/pages/Home'));
const MainThread = lazy(() => import('@/pages/MainThread'));
const Chat = lazy(() => import('@/pages/Chat'));
const Knowledge = lazy(() => import('@/pages/Knowledge'));
const KnowledgeDetail = lazy(() => import('@/pages/KnowledgeDetail'));
const ConnectedAccounts = lazy(() => import('@/pages/ConnectedAccounts'));
const ConnectedSites = lazy(() => import('@/pages/ConnectedSites'));
const PlaidOAuthReturn = lazy(() => import('@/pages/PlaidOAuthReturn'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys'));
const SharedChat = lazy(() => import('@/pages/SharedChat'));
const MeetupPoll = lazy(() => import('@/pages/MeetupPoll'));
const EventRsvp = lazy(() => import('@/pages/EventRsvp'));
const BusinessRoutes = lazy(() => import('@/features/orgs/BusinessRoutes'));
const Checkup = lazy(() => import('@/pages/Checkup'));
const CheckupResults = lazy(() => import('@/pages/CheckupResults'));
const ImportWizard = lazy(() => import('@/pages/ImportWizard'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Library = lazy(() => import('@/pages/Library'));
const AssetViewerOverlay = lazy(() =>
  import('@/components/library/AssetViewerOverlay').then((m) => ({ default: m.AssetViewerOverlay }))
);

/**
 * Inner app component that uses hooks requiring QueryClient context.
 */
function AppContent() {
  const { t } = useTranslation();

  // Enable network status monitoring for offline/online detection
  // Shows toast notifications and queues requests for retry
  useNetworkStatus();

  // Initialize authentication - must complete before rendering protected routes
  const { isInitialized, isAuthenticated, login, register, user } = useAuth();

  // Report first-touch acquisition attribution once after the first successful
  // auth. user.id is the Keycloak subject claim (see getUserInfo in lib/keycloak).
  useAttributionReport(isInitialized && isAuthenticated, user?.id);

  // Once per browser after login: move an old browser-only "notifications
  // off" to the server side preference (best effort, never throws).
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      void migrateNotifyPreference();
    }
  }, [isInitialized, isAuthenticated]);

  // Global session expiration handling
  // Listens for rosie:session-expired events and shows re-auth modal
  const { state, redirectToLogin, dismissModal } = useSessionExpiration();

  // Memoized so the hook's BroadcastChannel effect is not torn down and
  // rebuilt on every render of this component. A logout message arriving in
  // that gap would be dropped.
  const handleCrossTabLogout = useCallback(() => {
    toast.info({
      title: t('auth.loggedOutOtherTab'),
      description: t('auth.loggedOutOtherTabDescription'),
    });
  }, [t]);

  // Cross-tab session sync. Mounted here, above the router, so it covers every
  // authenticated route including /api-keys (which does not render AppLayout).
  // Without this mount the logout BroadcastChannel that keycloak.logout()
  // posts on had no listener at all, so signing out in one tab left every
  // other tab fully authenticated (A.08).
  //
  // refreshOnFocus is off deliberately: useAuth already refreshes the token on
  // a 30 second interval, so the hook's focus-refresh path would be pure
  // duplication and pure added risk on a session-security fix.
  useCrossTabSession({
    refreshOnFocus: false,
    onCrossTabLogout: handleCrossTabLogout,
  });

  return (
    // useTransitions={false}: react-router 7 wraps every location update in
    // React.startTransition by default. v6 (what production ran) did not, and
    // the app's navigation, stream state and lazy route fallbacks were built
    // against synchronous location updates, so keep that timing.
    <BrowserRouter useTransitions={false}>
      {/* Re-authentication modal - shown globally when session expires */}
      <ReauthModal
        open={state.showReauthModal}
        onLogin={redirectToLogin}
        onDismiss={dismissModal}
        isLoading={state.isRefreshing}
      />

      <Suspense fallback={<LoadingSpinner fullscreen />}>
        <Routes>
          {/* Shared chat (public, no auth required) */}
          <Route path="/s/:shareId" element={<SharedChat />} />

          {/* Meetup poll (public, no auth required; friends vote here) */}
          <Route path="/meetup/:token" element={<MeetupPoll />} />

          {/* Shared event RSVP (public, no auth required; guests respond here) */}
          <Route path="/rsvp/:token" element={<EventRsvp />} />

          {/* Sign up entry for marketing buttons: guests go straight to the
              Keycloak registration form (never the sign in form), signed in
              users go to the app. Must stay above the "*" AuthGate route. */}
          <Route
            path="/signup"
            element={
              <SignupGate
                isInitialized={isInitialized}
                isAuthenticated={isAuthenticated}
                onRegister={register}
              />
            }
          />

          {/* All other routes require auth to be initialized */}
          <Route
            path="*"
            element={
              <AuthGate
                isInitialized={isInitialized}
                isAuthenticated={isAuthenticated}
                onLogin={login}
              />
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/**
 * Gate component that waits for auth initialization before rendering protected routes.
 * Shows loading spinner while Keycloak initializes, then renders routes or redirects to login.
 */
function AuthGate({
  isInitialized,
  isAuthenticated,
  onLogin,
}: {
  isInitialized: boolean;
  isAuthenticated: boolean;
  onLogin: () => void;
}) {
  // Trigger login when initialized but not authenticated
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      onLogin();
    }
  }, [isInitialized, isAuthenticated, onLogin]);

  // Show loading spinner while Keycloak initializes or while redirecting to login
  if (!isInitialized || !isAuthenticated) {
    return <LoadingSpinner fullscreen />;
  }

  // User is authenticated - render the protected routes. RealtimeConnection
  // opens the single Socket.IO connection that every realtime subscriber in
  // the tree below relies on; it is gone again the moment this gate closes.
  return (
    <>
      <RealtimeConnection />
      <AuthenticatedRoutes />
    </>
  );
}

/**
 * Renders the protected route tree with the Library asset viewer overlay
 * (plan 7.1): when the current navigation carries `state.backgroundLocation`
 * (set by `useOpenLibraryAsset`), the main `<Routes>` keeps rendering that
 * background location instead of `/library/:assetId`, so the chat or
 * Library page underneath stays mounted (scroll position, streaming state)
 * while a second, overlay-only `<Routes>` renders the overlay on top.
 */
/** Reads `:assetId` from the route so `AssetViewerOverlay` stays a plain, testable component. */
function LibraryAssetViewerRoute({ standalone = false }: { standalone?: boolean }) {
  const { assetId } = useParams<{ assetId: string }>();
  if (!assetId) return null;
  return <AssetViewerOverlay assetId={assetId} standalone={standalone} />;
}

/** The emailed invitation link is /join?token=...; the accept page lives under /business. */
function JoinRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/business/join${search}`} replace />;
}

function AuthenticatedRoutes() {
  const location = useLocation();
  const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)
    ?.backgroundLocation;

  return (
    <WorkspaceScope>
      <Routes location={backgroundLocation ?? location}>
        {/* Home: the main chat (also at /main). Both honor ?focus=<message id>,
            which push notifications and automation emails link to. */}
        <Route path="/" element={<MainThread />} />
        <Route path="/main" element={<MainThread />} />

        {/* Side chats list */}
        <Route path="/chats" element={<Home />} />

        {/* Individual chat */}
        <Route path="/chat/:id" element={<Chat />} />

        {/* Knowledge bases */}
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/knowledge/:id" element={<KnowledgeDetail />} />

        {/* The Scheduled Tasks page is gone; standing instructions live in the main chat. */}
        <Route path="/scheduled-tasks" element={<Navigate to="/" replace />} />
        {/* Library: everything Andi made for the user and everything the
            user stored (/library/:assetId renders this same page with the
            overlay open standalone, when there is no background route) */}
        <Route path="/library" element={<Library />} />
        <Route
          path="/library/:assetId"
          element={
            <>
              <Library />
              {!backgroundLocation && <LibraryAssetViewerRoute standalone />}
            </>
          }
        />

        {/* The Settings page is gone: Andi's look and personality open from Andi's
            face in the chat. Old bookmarks and links land on the main chat. */}
        <Route path="/settings/*" element={<Navigate to="/" replace />} />

        {/* Connected Accounts */}
        <Route path="/connected-accounts" element={<ConnectedAccounts />} />
        <Route path="/connected-sites" element={<ConnectedSites />} />
        <Route path="/plaid-oauth-return" element={<PlaidOAuthReturn />} />

        {/* API key management */}
        <Route path="/api-keys" element={<ApiKeys />} />

        {/* Andi for Business (behind the build flag; with it off these paths are 404 as before) */}
        {isOrgsEnabled() && <Route path="/business/*" element={<BusinessRoutes />} />}
        {isOrgsEnabled() && <Route path="/join" element={<JoinRedirect />} />}

        {/* Free business checkup (personal) and the import wizard (business). Each page reads
            GET /checkup/availability and shows nothing when the server answers 404. */}
        <Route path="/checkup" element={<Checkup />} />
        <Route path="/checkup/:runId" element={<CheckupResults />} />
        <Route path="/import" element={<ImportWizard />} />
        <Route path="/import/:sessionId" element={<ImportWizard />} />

        {/* 404 - Catch all unmatched routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {backgroundLocation && (
        <Routes>
          <Route path="/library/:assetId" element={<LibraryAssetViewerRoute />} />
        </Routes>
      )}
    </WorkspaceScope>
  );
}

/**
 * Root application component with React Router v6 configuration.
 *
 * All pages are lazy-loaded for optimal code splitting and initial load performance.
 * The router uses BrowserRouter for clean URLs with HTML5 history API.
 *
 * Providers (outer to inner):
 * - QueryClientProvider: TanStack Query for server state management
 * - BrowserRouter: React Router for client-side navigation
 *
 * Features:
 * - Network status monitoring with offline/online detection
 * - Toast notifications for user feedback
 * - Web Vitals collection in development mode
 */
function App() {
  // Collect Web Vitals metrics (FCP, LCP, CLS, TTFB)
  // Logs to console in development mode
  useWebVitals();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Catches uncaught render errors anywhere in the app and shows a
          "Something went wrong" fallback with a Reload button instead of
          leaving the user on a blank page. Without this, ErrorBoundary
          existed and was unit tested but was never actually mounted, so
          any render error crashed the whole React tree with no recovery
          UI (see the E2E test plan's N.04 case). */}
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
      {/* Toast notifications - renders at bottom right */}
      <Toaster />
      {/* React Query DevTools - only shown in development */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
